import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { ContentFragment } from 'client/site-modules/shared/components/content-fragment/content-fragment';
/* Constants */
import {
  PRIVATE_PARTY_SELL_CAR_CTA,
  CREATIVE_ID_ENTRY_PRIVATE_PARTY,
} from 'site-modules/shared/constants/private-party/private-party';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
/* Utils */
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { openLinkInNewTab } from 'site-modules/shared/utils/open-link-in-new-tab';
import { getCreateListingUrl } from 'site-modules/shared/utils/private-party/private-party';
/* Tracking */
import { EventToolbox } from 'client/utils/event-toolbox';
import { EngagementHandler } from 'client/engagement-handlers';
import { PrivatePartyEngagementHandler } from 'client/engagement-handlers/private-party-engagement-handler/private-party-engagement-handler';

import './private-party-sell-car-button.scss';

EngagementHandler.register(PrivatePartyEngagementHandler);

export function PrivatePartySellCarButton({
  isMobile = false,
  vin = null,
  modsId = null,
  receivedOffer = false,
  creativeId = CREATIVE_ID_ENTRY_PRIVATE_PARTY,
  content = {},
}) {
  const {
    imageSrc,
    benefits,
    header,
    headerMobile,
    description,
    headerClass = 'text-cool-gray-10',
    headerMobileClass = headerClass,
    descriptionClass = 'size-14',
    icon,
    imageSize = 40,
    imageSizeMobile = imageSize,
  } = content;
  const iconSize = isMobile ? imageSizeMobile : imageSize;
  const benefitsHeader = isMobile && headerMobile ? headerMobile : header;
  const url = getCreateListingUrl(vin, modsId, receivedOffer);

  const handleClick = useCallback(() => {
    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.START_PP_LISTING, {
      value: PRIVATE_PARTY_SELL_CAR_CTA,
      creativeId,
    });

    openLinkInNewTab(url);
  }, [creativeId, url]);

  return (
    <div
      data-tracking-parent={CREATIVE_ID_ENTRY_PRIVATE_PARTY}
      className="private-party-sell-car-wrapper rounded-12 bg-white d-md-inline-block w-100 mt-1"
    >
      <Button
        className={classnames(
          'text-decoration-none text-decoration-none-hover text-transform-none fw-normal d-flex justify-content-between align-items-center w-100 btn-wrapper',
          description ? 'px-0_5 px-md-2 py-0_75' : 'px-1 px-md-2 py-0_75'
        )}
        onClick={handleClick}
        color="link"
      >
        <div className={classnames('d-flex', { 'description-content align-items-center flex-grow-1': description })}>
          {icon ? (
            <i
              className={classnames(
                'sell-car-icon d-flex align-items-center justify-content-center flex-shrink-0 size-16 text-blue-50',
                icon,
                { 'sell-car-icon-mobile': isMobile }
              )}
              aria-hidden
            />
          ) : (
            <img
              src={getStaticImageUrl(imageSrc)}
              width={iconSize}
              height={description ? iconSize : ''}
              alt=""
              loading="lazy"
              className={description ? 'align-self-center flex-shrink-0' : ''}
            />
          )}
          <div
            className={classnames(
              description ? 'description-content flex-grow-1 ms-0_5 ms-md-1_5 me-md-1' : 'me-1 ms-1_5',
              'd-block text-start size-16 text-wrap text-md-nowrap'
            )}
          >
            <div className={classnames('fw-bold mb-0_25', isMobile ? headerMobileClass : headerClass)}>
              {benefitsHeader}
            </div>
            {description ? (
              <div className={classnames('text-wrap', descriptionClass)}>{description}</div>
            ) : (
              <ul className="ps-1 ps-lg-0 benefits-list size-14 mb-0 d-lg-flex text-start">
                {benefits.map((benefit, index) => (
                  <ContentFragment key={`benefit_${index + 1}`} classes="benefit-item" componentToUse="li">
                    {benefit}
                  </ContentFragment>
                ))}
              </ul>
            )}
          </div>
        </div>

        <i
          className={classnames('icon-arrow-right4 text-primary', { 'flex-shrink-0 ms-0_5 ms-md-0': description })}
          aria-hidden
        />
      </Button>
    </div>
  );
}

PrivatePartySellCarButton.propTypes = {
  content: PropTypes.shape({
    imageSrc: PropTypes.string,
    benefits: PropTypes.arrayOf(PropTypes.string),
    header: PropTypes.string,
    headerMobile: PropTypes.string,
    description: PropTypes.string,
    headerClass: PropTypes.string,
    headerMobileClass: PropTypes.string,
    descriptionClass: PropTypes.string,
    icon: PropTypes.string,
    imageSize: PropTypes.number,
    imageSizeMobile: PropTypes.number,
  }),
  isMobile: PropTypes.bool,
  vin: PropTypes.string,
  modsId: PropTypes.string,
  receivedOffer: PropTypes.bool,
  creativeId: PropTypes.string,
};
